import React from "react";
import { Link } from "react-router-dom";
import { getRole, getTier, openBillingPortal } from "../services/api";

/**
 * Pricing page — two commercial paths × three tiers each.
 *
 * Matches the marketing site: no fixed prices visible, every CTA is
 * "Request quote". Existing paid customers (Stripe subscribers) see a
 * "Manage billing" link below the cards.
 *
 * Tier-name → backend `subscription_tier` mapping for the "Current plan"
 * badge:
 *   Professional Subscription → pro
 *   Hotel Subscription        → business
 * (Enterprise + every licence option is custom — never matches the
 * backend tier flag, so we don't badge any current plan there.)
 *
 * The CTA is a plain mailto + WhatsApp deeplink, identical to the
 * marketing page. We don't try to wire up a quote-request form in the
 * app yet; manual sales conversation is the deliberate stage we're at.
 */

const SUBSCRIPTION_TIERS = [
  {
    key: "sub_pro",
    name: "Professional",
    sub: "Subscription",
    typicalFit: "Single-venue restaurant or café, up to ~15 tables, single language.",
    features: [
      "QR menu & ordering",
      "Live kitchen queue & waiter workflow",
      "Open Orders mode for multi-round service",
      "Menu management + dietary tags",
      "Food & service ratings",
      "Standard support",
    ],
    backendTier: "pro",
  },
  {
    key: "sub_hotel",
    name: "Hotel",
    sub: "Subscription",
    badge: "Most popular",
    typicalFit: "Hotel with restaurant + room service + bar, multiple service areas, multi-language menus.",
    features: [
      "Everything in Professional",
      "Multi-area service setup (rooms + bar + restaurant)",
      "Smart Recommendations (chef picks + customer favorites + ordered-together)",
      "Manager dashboard with full analytics",
      "Custom branding (logo, cover)",
      "Priority support",
    ],
    backendTier: "business",
    highlight: true,
  },
  {
    key: "sub_enterprise",
    name: "Enterprise",
    sub: "Subscription",
    typicalFit: "Multi-branch group or 50+ tables, custom integrations, dedicated support.",
    features: [
      "Everything in Hotel",
      "Multi-branch / multi-property setup",
      "Custom integrations (POS, payments, BI)",
      "Dedicated onboarding session",
      "Account manager",
      "Custom SLA",
    ],
  },
];

const LICENCE_TIERS = [
  {
    key: "lic_pro",
    name: "Professional",
    sub: "Licence",
    typicalFit: "Single venue that prefers a customer-owned deployment.",
    features: [
      "Dedicated ODRA install",
      "Customer cloud or local server",
      "All Professional Subscription features",
      "One-time licence agreement",
      "Optional annual support",
      "Paid upgrades when needed",
    ],
  },
  {
    key: "lic_hotel",
    name: "Hotel",
    sub: "Licence",
    typicalFit: "Hotel-scale deployment with annual support and dedicated environment.",
    features: [
      "Dedicated ODRA install (hotel-scale)",
      "All Hotel Subscription features",
      "Customer cloud or on-prem server",
      "Annual support agreement (separate)",
      "Major upgrades sold separately",
      "Custom deployment review",
    ],
  },
  {
    key: "lic_enterprise",
    name: "Enterprise",
    sub: "Licence",
    typicalFit: "Multi-property group with custom infrastructure or compliance needs.",
    features: [
      "Dedicated multi-site deployment",
      "All Enterprise Subscription features",
      "Custom integrations & data residency",
      "Bespoke support agreement",
      "Custom upgrade cadence",
      "On-site visits as agreed",
    ],
  },
];

// Quote-request deeplinks (match marketing site CTAs)
const MAILTO_QUOTE = (tier) =>
  `mailto:hello@odra.app?subject=${encodeURIComponent("ODRA quote — " + tier.name + " " + tier.sub)}`;
const WHATSAPP_QUOTE = (tier) =>
  `https://wa.me/31618241608?text=${encodeURIComponent(
    "Hi ODRA team, I'd like a quote for the " + tier.name + " " + tier.sub + " package."
  )}`;


function TierCard({ tier, currentBackendTier }) {
  const isCurrent =
    tier.backendTier && tier.backendTier === currentBackendTier;
  return (
    <div
      className="card"
      style={{
        position: "relative",
        padding: 22,
        border: tier.highlight ? "2px solid var(--brand, #d4af37)" : undefined,
        borderRadius: 14,
        background: "#fff",
        boxShadow: tier.highlight ? "0 6px 24px rgba(212, 175, 55, 0.18)" : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {tier.badge && (
        <div
          style={{
            position: "absolute",
            top: -11,
            right: 16,
            background: "#d4af37",
            color: "#fff",
            fontSize: "0.68rem",
            fontWeight: 700,
            padding: "3px 12px",
            borderRadius: 999,
            letterSpacing: 0.7,
            textTransform: "uppercase",
          }}
        >
          {tier.badge}
        </div>
      )}

      <div>
        <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.8, color: "#6b7280", fontWeight: 700 }}>
          {tier.sub}
        </div>
        <h3 style={{ marginTop: 4, marginBottom: 0, fontSize: "1.4rem" }}>{tier.name}</h3>
      </div>

      <p className="muted" style={{ margin: 0, fontSize: "0.88rem", lineHeight: 1.4, minHeight: 56 }}>
        {tier.typicalFit}
      </p>

      <ul style={{ listStyle: "none", padding: 0, margin: "4px 0", flex: 1 }}>
        {tier.features.map((f, i) => (
          <li key={i} style={{ padding: "4px 0", fontSize: "0.88rem", display: "flex", gap: 8 }}>
            <span style={{ color: "#008080", fontWeight: 700, flexShrink: 0 }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <div
          style={{
            border: "1px solid #008080",
            color: "#008080",
            padding: "10px 12px",
            borderRadius: 8,
            textAlign: "center",
            fontWeight: 600,
            fontSize: "0.9rem",
          }}
        >
          ✓ Your current plan
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <a
            href={MAILTO_QUOTE(tier)}
            className="btn"
            style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
          >
            Request quote
          </a>
          <a
            href={WHATSAPP_QUOTE(tier)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn secondary"
            title="Contact on WhatsApp"
            aria-label="Contact on WhatsApp"
            style={{ width: 44, padding: "10px 0", textAlign: "center" }}
          >
            <span aria-hidden="true">💬</span>
          </a>
        </div>
      )}
    </div>
  );
}


export default function PricingPage() {
  const role = getRole();
  const tier = getTier(); // backend value: free / pro / business

  const onPortal = async () => {
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not open billing portal.");
    }
  };

  return (
    <div className="page" style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ textAlign: "center", margin: "28px 0 12px" }}>
        <p style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 1.2, color: "#a8892a", fontWeight: 700, marginBottom: 6 }}>
          Two ways to work with ODRA
        </p>
        <h1 style={{ marginBottom: 8, fontSize: "2rem" }}>Choose the model that fits your operation</h1>
        <p className="muted" style={{ fontSize: "1rem", maxWidth: 700, margin: "0 auto" }}>
          Monthly subscription for a fully managed service. One-time licence
          for businesses that prefer a dedicated, customer-owned deployment.
        </p>
      </div>

      {/* Monthly Subscription path */}
      <section style={{ marginTop: 32 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: "1.3rem" }}>Monthly Subscription</h2>
          <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.8, color: "#d4af37", fontWeight: 700, background: "#fff8e7", padding: "3px 10px", borderRadius: 999 }}>
            Best for managed service
          </span>
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 16, fontSize: "0.9rem" }}>
          ODRA hosted, maintained, and updated by our team. Support and updates included.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {SUBSCRIPTION_TIERS.map((t) => (
            <TierCard key={t.key} tier={t} currentBackendTier={role ? tier : null} />
          ))}
        </div>
      </section>

      {/* One-Time Licence path */}
      <section style={{ marginTop: 40 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: "1.3rem" }}>One-Time Licence Purchase</h2>
          <span style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.8, color: "#063B48", fontWeight: 700, background: "#e8f2f4", padding: "3px 10px", borderRadius: 999 }}>
            Best for dedicated deployment
          </span>
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 16, fontSize: "0.9rem" }}>
          A dedicated ODRA installation. Customer owns the deployment. Support,
          updates, and upgrades governed by a separate agreement.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {LICENCE_TIERS.map((t) => (
            <TierCard key={t.key} tier={t} currentBackendTier={null} />
          ))}
        </div>
      </section>

      {/* Tailored-pricing note */}
      <div
        className="card"
        style={{
          marginTop: 32,
          padding: 20,
          background: "#fbf9f4",
          borderColor: "#f0d38b",
          textAlign: "center",
        }}
      >
        <p style={{ margin: 0, fontSize: "0.95rem" }}>
          <b>Pricing is tailored</b> based on your business size, number of tables
          or service areas, support needs, and deployment preference.
        </p>
        <p className="muted" style={{ margin: "6px 0 0", fontSize: "0.85rem" }}>
          Email <a href="mailto:hello@odra.app" style={{ color: "#008080" }}>hello@odra.app</a> or
          WhatsApp <a href="https://wa.me/31618241608" target="_blank" rel="noopener noreferrer" style={{ color: "#008080" }}>+31&nbsp;6&nbsp;1824&nbsp;1608</a> to start the conversation.
        </p>
      </div>

      {/* Existing-customer billing management */}
      {role && tier && tier !== "free" && (
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <p className="muted" style={{ marginBottom: 8 }}>
            Already a customer with a Stripe subscription?
          </p>
          <button type="button" className="btn secondary" onClick={onPortal}>
            Manage billing
          </button>
        </div>
      )}

      <p className="muted" style={{ textAlign: "center", marginTop: 36, fontSize: "0.85rem" }}>
        {role ? "Signed in." : <>Already a customer? <Link to="/login">Sign in</Link>.</>}
      </p>
    </div>
  );
}
